import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaSpinner } from 'react-icons/fa';
import { ModelCardProps } from './types';
import { getModelTypeColor, getModelTypeAccent, getFileCategory } from './utils';

/**
 * ModelCard sub-component
 * Displays a single model with download button
 */
export const ModelCard: React.FC<ModelCardProps> = memo(({
  model,
  downloading,
  downloadingAll,
  onDownload,
  formatDate
}) => {
  const { t } = useTranslation('training');
  const isDownloading = downloading === model.filename;
  const isDisabled = isDownloading || downloadingAll;

  return (
    <div className={`
      group relative bg-card rounded-lg border transition-all duration-150
      ${isDownloading
        ? 'border-primary/20 shadow-sm ring-1 ring-primary/10'
        : 'border-border hover:border-border/60 hover:shadow-sm'
      }
    `}>
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${getModelTypeAccent(model.model_type, model.filename)}`} />

      <div className="flex items-center justify-between p-4 pl-5">
        <div className="flex-1 min-w-0 mr-4">
          {/* Model name + badges */}
          <div className="flex items-center gap-2.5 mb-1.5">
            <h3 className="text-sm font-semibold text-foreground truncate">
              {model.filename}
            </h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide uppercase ${getModelTypeColor(model.model_type, model.filename)}`}>
              {t(`training.modelDownload.category.${getFileCategory(model.filename)}`)}
            </span>
          </div>

          {/* Metadata row */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <span className="font-medium text-foreground">{model.format.toUpperCase()}</span>
            </span>
            <span className="w-px h-3 bg-border" />
            <span>
              {model.file_size_mb} MB
            </span>
            <span className="w-px h-3 bg-border" />
            <span>
              {formatDate(model.modified_time)}
            </span>
          </div>
        </div>

        {/* Download button */}
        <button
          type="button"
          onClick={() => onDownload(model)}
          disabled={isDisabled}
          className={`
            flex-shrink-0 flex items-center gap-2 text-xs font-medium px-3.5 py-2 rounded-lg transition-all duration-150
            ${isDownloading
              ? 'bg-accent text-primary cursor-wait'
              : isDisabled
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-muted text-foreground hover:bg-primary hover:text-primary-foreground active:scale-[0.97]'
            }
          `}
        >
          {isDownloading ? (
            <>
              <FaSpinner className="animate-spin text-[10px]" />
              <span>{t('training.modelDownload.downloading')}</span>
            </>
          ) : (
            <>
              <FaDownload className="text-[10px]" />
              <span>{t('training.modelDownload.download')}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
});

ModelCard.displayName = 'ModelCard';

export default ModelCard;
